// src/components/Dashboard.js
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  runTransaction,
} from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import CONFIG from '../config';
import { AuthContext } from '../contexts/AuthContext';
import { 
  Video,
  ExternalLink, 
  Copy, 
  Trash2,
  Pencil,
  AlarmClock, 
  Film, 
  Eye,
  BarChart4,
  Star,
  AlertTriangle,
  Zap
} from 'lucide-react';
import { logVideoDeletion } from '../utils/videoEvents';
import { logShareAction } from '../utils/activityLogger';

import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { OnboardingChecklist } from './ui/OnboardingChecklist';

function Dashboard() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [copiedVideoId, setCopiedVideoId] = useState(null);
  const [copiedEmailId, setCopiedEmailId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ... rest of state and effects ...

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUserData(user.uid);

        const videosRef = collection(db, 'videos');
        const q = query(
          videosRef,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

        const unsubscribeVideos = onSnapshot(
          q,
          (querySnapshot) => {
            const videosList = querySnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setVideos(videosList);
            setLoading(false);
          },
          (error) => {
            toast.error(CONFIG.ui.toasts.fetchUserDataFailed);
            setLoading(false);
          }
        );

        return () => unsubscribeVideos();
      } else {
        setUserData(null);
        setVideos([]);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserData = async (userId) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userSnapshot = await getDoc(userDocRef);
      if (userSnapshot.exists()) {
        setUserData(userSnapshot.data());
      } else {
        toast.error(CONFIG.ui.toasts.userDataNotFound);
      }
    } catch (error) {
      toast.error(CONFIG.ui.toasts.fetchUserDataFailed);
    }
  };

  const handleCopyLink = async (videoId) => {
    const link = `${window.location.origin}/${auth.currentUser.uid}/introreel/${videoId}`;
    navigator.clipboard.writeText(link);
    setCopiedVideoId(videoId);
    toast.success('Link copied to clipboard!');
    logShareAction(videoId, 'link');
    
    // Track completion for onboarding
    if (!userData?.hasCopiedLink) {
      try {
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userDocRef, { hasCopiedLink: true });
        setUserData(prev => ({ ...prev, hasCopiedLink: true }));
      } catch (e) {}
    }
    
    setTimeout(() => setCopiedVideoId(null), 2000);
  };
  
  const handleCopyEmail = async (videoId, videoTitle, thumbnailUrl) => {
    const tempDiv = document.createElement('div');
    const link = `${window.location.origin}/${auth.currentUser.uid}/introreel/${videoId}`;
    const creatorName = userData?.fullName || auth.currentUser.displayName || auth.currentUser.email || 'me';
    
    tempDiv.innerHTML = `
      <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; max-width: 500px; font-family: Arial, sans-serif;">
        <div style="font-weight: 600; font-size: 18px; margin-bottom: 12px;">${videoTitle || 'My Video Introduction'}</div>
        <div style="display: flex; align-items: center; margin-bottom: 16px;">
          <div style="width: 120px; height: 68px; margin-right: 16px; overflow: hidden; border-radius: 4px;">
            <img src="${thumbnailUrl || '/default-thumbnail.jpg'}" alt="Video thumbnail" style="width: 100%; height: 100%; object-fit: cover;" />
          </div>
          <div>
            <p style="margin: 0 0 8px 0; font-size: 14px;">A video introduction by ${creatorName}</p>
            <a href="${link}" style="display: inline-block; background-color: #2563eb; color: white; font-weight: 500; padding: 6px 12px; border-radius: 4px; text-decoration: none; font-size: 14px;">Watch Video</a>
          </div>
        </div>
        <div style="font-size: 12px; color: #6b7280; text-align: center;">Created with <a href="https://reelintro.com" style="color: #2563eb; text-decoration: none;">ReelIntro</a></div>
      </div>
    `;
    
    try {
      if (navigator.clipboard && navigator.clipboard.write) {
        const blob = new Blob([tempDiv.innerHTML], { type: 'text/html' });
        const data = [new ClipboardItem({ 'text/html': blob })];
        navigator.clipboard.write(data);
      } else {
        document.body.appendChild(tempDiv);
        const range = document.createRange();
        range.selectNode(tempDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        document.body.removeChild(tempDiv);
      }
      
      setCopiedEmailId(videoId);
      toast.success('Email content copied to clipboard!');
      logShareAction(videoId, 'email');

      // Track completion for onboarding
      if (!userData?.hasCopiedLink) {
        try {
          const userDocRef = doc(db, 'users', auth.currentUser.uid);
          await updateDoc(userDocRef, { hasCopiedLink: true });
          setUserData(prev => ({ ...prev, hasCopiedLink: true }));
        } catch (e) {}
      }

      setTimeout(() => setCopiedEmailId(null), 2000);
    } catch (err) {
      const text = `Check out my video introduction: ${videoTitle || 'Video Introduction'}\n\nWatch here: ${link}\n\nCreated with ReelIntro`;
      navigator.clipboard.writeText(text);
      setCopiedEmailId(videoId);
      toast.success('Email content copied to clipboard (text only)');
      logShareAction(videoId, 'email_text');
      setTimeout(() => setCopiedEmailId(null), 2000);
    }
  };

  const handleSetPrimary = async (videoId) => {
    if (!auth.currentUser) return;
    try {
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userDocRef, {
        primaryVideoId: videoId
      });
      setUserData(prev => ({ ...prev, primaryVideoId: videoId }));
      toast.success('🎉 Primary video set! Your Personal Brand Link is active.');
    } catch (error) {
      console.error('Error setting primary video:', error);
      toast.error('Failed to update primary video.');
    }
  };

  const handleDelete = async (videoId, videoUrl, thumbnailUrl) => {
    const user = auth.currentUser;
    if (!user) return;

    const isPrimary = userData?.primaryVideoId === videoId;
    const hasVanityId = !!userData?.vanityId;
    
    let confirmMessage = CONFIG.ui.toasts.confirmDelete;
    
    // Scenario: Deleting primary video when a vanity URL exists
    if (isPrimary && hasVanityId) {
      confirmMessage = "⚠️ WARNING: This is your Primary Video. Deleting it will BREAK your Personal Brand Link (reelintro.com/" + userData.vanityId + ") until you set a new one. Are you sure?";
    }

    if (window.confirm(confirmMessage)) {
      try {
        const videoDocRef = doc(db, 'videos', videoId);
        const userDocRef = doc(db, 'users', user.uid);
        const videoSnapshot = await getDoc(videoDocRef);
        const videoData = videoSnapshot.exists() ? videoSnapshot.data() : {};
        
        await runTransaction(db, async (transaction) => {
          const videoDoc = await transaction.get(videoDocRef);
          if (!videoDoc.exists()) throw new Error('Video does not exist.');

          const userDoc = await transaction.get(userDocRef);
          transaction.delete(videoDocRef);
          
          const updateData = {
            videoCount: Math.max((userDoc.data().videoCount || 0) - 1, 0)
          };
          
          if (isPrimary) {
            updateData.primaryVideoId = null;
          }
          
          transaction.update(userDocRef, updateData);
        });

        await deleteObject(storageRef(storage, videoUrl));
        await deleteObject(storageRef(storage, thumbnailUrl));
        await logVideoDeletion(videoId, videoData);

        toast.success(CONFIG.ui.toasts.videoDeleted);
        
        // Post-deletion guidance
        if (isPrimary && hasVanityId) {
          if (videos.length > 1) {
            toast.info("Your Personal Brand Link is now empty. Please set another video as primary.", { autoClose: 10000 });
          } else {
            toast.warn("You have no videos left. Record a new one to restore your Personal Brand Link.", { autoClose: 10000 });
          }
        }
      } catch (error) {
        toast.error(`Failed to delete video: ${error.message}`);
      }
    }
  };

  const videoLimitReached =
    userData &&
    videos.length >= (CONFIG.tiers[userData.subscriptionTier]?.maxUploads || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <ClipLoader size={50} color="#1E40AF" loading={loading} />
      </div>
    );
  }

  const { subscriptionTier } = useContext(AuthContext);
  const planConfig = CONFIG.tiers[subscriptionTier] || {};
  const isFree = subscriptionTier === 'free';
  
  const totalVideosCount = videos.length;
  const maxVideos = planConfig.maxUploads || 0;
  const usagePercentage = maxVideos ? Math.min(100, Math.round((totalVideosCount / maxVideos) * 100)) : 0;
  const remainingVideos = maxVideos ? maxVideos - totalVideosCount : 0;
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* New Onboarding Launchpad */}
      <OnboardingChecklist userData={userData} videos={videos} />

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Videos</h1>
          <p className="text-gray-600 mt-1">Manage your video introductions</p>
        </div>
        
        {!videoLimitReached ? (
          <Button 
            onClick={() => navigate('/record')}
            icon={Video}
          >
            Record New Video
          </Button>
        ) : (
          <div className="flex items-center space-x-2">
            <Button variant="ghost" disabled icon={Video}>
              Record New Video
            </Button>
            <Button 
              variant="secondary"
              onClick={() => navigate('/profile?upgrade=true')}
              icon={Zap}
            >
              Upgrade Plan
            </Button>
          </div>
        )}
      </div>
      
      {/* Video Usage Card */}
      <Card className="mb-8">
        <div className="flex md:flex-row flex-col md:items-end justify-between mb-6">
          <div>
            <div className="flex items-center mb-3">
              <h2 className="text-lg font-medium text-gray-900 mr-2">Video Usage</h2>
              <Film className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">{totalVideosCount}</span>
              <span className="text-gray-500 ml-1">/ {maxVideos || '∞'} videos</span>
            </div>
          </div>
          
          <div className="md:text-right md:mt-0 mt-4">
            <p className="text-sm text-gray-700 mb-1">Your plan includes:</p>
            <div className="flex md:justify-end space-x-4">
              <span className="inline-flex items-center text-sm text-gray-600">
                <span className="w-2 h-2 mr-1 rounded-full bg-blue-600"></span>
                {planConfig.maxRecordTime || 0}s recording time
              </span>
              <span className="inline-flex items-center text-sm text-gray-600">
                <span className="w-2 h-2 mr-1 rounded-full bg-green-600"></span>
                {planConfig.quality || 'Standard'} quality
              </span>
            </div>
          </div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-blue-600 h-2.5 rounded-full" 
            style={{ width: `${usagePercentage}%` }}
          ></div>
        </div>
        <p className="mt-2 text-sm text-gray-600 text-right">
          {videoLimitReached 
            ? 'You\'ve reached your video limit' 
            : `${remainingVideos} videos remaining on your plan`
          }
        </p>
      </Card>

      {/* Videos Content */}
      <Card 
        padding="p-0"
        header={<h2 className="text-lg font-medium text-gray-900">Your Videos</h2>}
      >
        {videos.length === 0 ? (
          <div className="flex flex-col items-center p-12 text-center">
            <div className="bg-gray-100 rounded-full p-3 mb-4">
              <Video className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No videos yet</h3>
            <p className="text-gray-600 mb-6 max-w-md">
              Get started by recording your first video introduction. It only takes a minute!
            </p>
            <Button onClick={() => navigate('/record')}>
              Record Your First Video
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 divide-y divide-gray-200">
            {videos.map((video) => (
              <div key={video.id} className={`p-6 transition-colors ${userData?.primaryVideoId === video.id ? 'bg-blue-50/30' : 'hover:bg-gray-50'}`}>
                <div className="flex flex-col md:flex-row md:items-center">
                  <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
                    <div className={`relative w-full md:w-40 h-24 bg-gray-200 rounded-md overflow-hidden ${userData?.primaryVideoId === video.id ? 'ring-2 ring-blue-500' : ''}`}>
                      <img
                        src={video.thumbnailUrl}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                  <div className="flex-grow">
                    <div className="flex items-center mb-1">
                      <h3 className="text-lg font-medium text-gray-900 mr-2">{video.title}</h3>
                      {userData?.primaryVideoId === video.id && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-600 text-white shadow-sm">
                          <Star className="w-3 h-3 mr-1 fill-current" /> PRIMARY BRAND VIDEO
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-3">
                      Created: {video.createdAt?.toDate().toLocaleDateString() || 'Unknown date'}
                    </p>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/${auth.currentUser.uid}/introreel/${video.id}`)}
                        icon={Eye}
                      >
                        View
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleCopyLink(video.id)}
                        icon={Copy}
                      >
                        {copiedVideoId === video.id ? 'Copied!' : 'Copy Link'}
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleCopyEmail(video.id, video.title, video.thumbnailUrl)}
                        icon={Film}
                      >
                        {copiedEmailId === video.id ? 'Copied!' : 'Copy for Email'}
                      </Button>
                      
                      <Button 
                        variant={userData?.primaryVideoId === video.id ? 'primary' : 'ghost'} 
                        size="sm"
                        onClick={() => handleSetPrimary(video.id)}
                        disabled={userData?.primaryVideoId === video.id}
                        icon={Star}
                      >
                        {userData?.primaryVideoId === video.id ? 'Primary' : 'Set as Primary'}
                      </Button>
                      
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDelete(video.id, video.videoUrl, video.thumbnailUrl)}
                        icon={Trash2}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default Dashboard;
